import { useRef } from "react";
import { Input } from "antd";
import { t } from "i18next";

/**
 * Birthdate entered as three boxes (DD / MM / YYYY) — same UX on desktop and mobile (no native date picker).
 * Accepts leading zeros ("01"), restricts each box to digits, and auto-advances to the next box once a box is
 * filled (day/month max 2 chars, year max 4). Backspace on an empty box jumps back to the previous one.
 *
 * Controlled by antd Form via { value, onChange } where value is { day, month, year } (all strings).
 */
export default function BirthDateInput({ value = {}, onChange }) {
    const dayRef = useRef(null);
    const monthRef = useRef(null);
    const yearRef = useRef(null);

    const emit = (changed) => {
        onChange?.({ day: value.day || '', month: value.month || '', year: value.year || '', ...changed });
    };

    const handle = (field, max, nextRef) => (e) => {
        const v = e.target.value.replace(/\D/g, '').slice(0, max);
        emit({ [field]: v });
        if (v.length === max && nextRef?.current) {
            nextRef.current.focus();
        }
    };

    const handleKeyDown = (prevRef) => (e) => {
        if (e.key === 'Backspace' && !e.target.value && prevRef?.current) {
            prevRef.current.focus();
        }
    };

    const boxStyle = { textAlign: 'center' };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
            <Input
                ref={dayRef}
                size="large"
                inputMode="numeric"
                maxLength={2}
                placeholder={t('DD')}
                style={boxStyle}
                value={value.day || ''}
                onChange={handle('day', 2, monthRef)}
            />
            <span style={{ color: '#bfbfbf' }}>/</span>
            <Input
                ref={monthRef}
                size="large"
                inputMode="numeric"
                maxLength={2}
                placeholder={t('MM')}
                style={boxStyle}
                value={value.month || ''}
                onChange={handle('month', 2, yearRef)}
                onKeyDown={handleKeyDown(dayRef)}
            />
            <span style={{ color: '#bfbfbf' }}>/</span>
            <Input
                ref={yearRef}
                size="large"
                inputMode="numeric"
                maxLength={4}
                placeholder={t('YYYY')}
                style={boxStyle}
                value={value.year || ''}
                onChange={handle('year', 4, null)}
                onKeyDown={handleKeyDown(monthRef)}
            />
        </div>
    );
}
